import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import classNames from 'clsx';

import { INLINE_PLAYER_INSTANCE_KEY } from '@/modules/common/components/Player/constants';
import { NEXT_CLIP_COMPLETE_EVENT } from '@/modules/common/components/Player/constants/events';

import { ChannelType } from '@/modules/common/types/channel';
import { VideoType } from '@/modules/common/types/video';

import { useOnScreen } from '@/modules/common/helpers/customHooks';
import { throttle } from '@/modules/common/helpers/events';

import Player from '../../../../common/components/Player/Player';

import styles from './VideoPlayer.module.scss';

type Props = {
  className?: string;
  video: VideoType;
  channel: ChannelType;
  onEnded: () => void;
};

function Video({ className = '', video, channel, onEnded }: Props) {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const inView = useOnScreen(videoRef, true);
  const [dimensions, setDimensions] = useState<Record<string, string | undefined>>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const fn = () => {
      if (videoRef.current) {
        const { clientWidth, clientHeight } = videoRef.current;

        const width: number = clientHeight * (16 / 9);
        const height: number = clientHeight;

        setDimensions({
          width: `${clientWidth > width ? clientWidth : width}px`,
          height: `${clientWidth > width ? width / (16 / 9) : height}px`,
        });
      }
    };

    fn();

    const throttleFn = throttle(fn, 1000);

    window.addEventListener('resize', throttleFn);

    return () => {
      window.removeEventListener('resize', throttleFn);
    };
  }, []);

  return (
    <div className={classNames(styles.Wrapper, className)} ref={videoRef} data-a="player-video-wrapper">
      {inView && (
        <Player
          className={styles.Player}
          showLoader={false}
          instanceKey={INLINE_PLAYER_INSTANCE_KEY}
          video={video}
          playerPropsPassThrow={channel.playerPropsPassThrow}
          clipSource={channel.playlistId}
          listeners={{
            [NEXT_CLIP_COMPLETE_EVENT]: () => onEnded(),
          }}
          style={
            {
              width: dimensions.width,
              height: dimensions.height,
            } as CSSProperties
          }
        />
      )}
    </div>
  );
}

export default Video;
